import {
  IndentedRowWrapper,
  SmallInputWrapper,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import { ExpandableCheckbox } from 'modules/workflowNodeEditors/editorComponents';
import { RateLimit } from 'modules/workflows/types';
import { useState } from 'react';
import { useUpdateEffect } from 'react-use';
import noop from 'utils/noop';

import { RATE_LIMIT_UNITS } from '../../BaseSendEditor.constants';
import { ErrorText } from '../../BaseSendEditor.styles';
import { DEFAULT_LIMIT, DEFAULT_UNIT, rateUnitOptions } from './RateLimitingCheckbox.constants';
import { SeparationText, TinyBasicSelect, WideInput } from './RateLimitingCheckbox.styles';
import {
  createLimitSetting,
  getRateLimitPerMin,
  validateLimit,
} from './RateLimitingCheckbox.utils';

export interface RateLimitingCheckboxProps {
  rateLimit?: RateLimit | null;
  onChange?: (rateLimit: any) => void;
  isDisabled?: boolean;
}

export const RATE_LIMIT_TEST_ID = 'rate-limiting-test-id';
export const RATE_LIMIT_INPUT_TEST_ID = 'rate-limit-input';

export const RateLimitingCheckbox = ({
  rateLimit,
  onChange = noop,
  isDisabled = false,
}: RateLimitingCheckboxProps) => {
  const { useRateLimit = false, rateLimitPerMin = DEFAULT_LIMIT } = (rateLimit as RateLimit) ?? {};
  const [isChecked, setIsChecked] = useState(useRateLimit === true);
  const [visibleLimit, setVisibleLimit] = useState(rateLimitPerMin);
  const [unit, setUnit] = useState(DEFAULT_UNIT);
  const [rangeError, setRangeError] = useState(validateLimit(visibleLimit, unit));

  const onUnitSelect = (selectedUnit: RATE_LIMIT_UNITS) => {
    if (selectedUnit !== unit) {
      setUnit(selectedUnit as RATE_LIMIT_UNITS);
      setRangeError(validateLimit(visibleLimit, selectedUnit));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setVisibleLimit(newLimit);
    setRangeError(validateLimit(newLimit, unit));
  };

  const handleLimitUpdate = () => {
    const updatedRateLimitPerMin = getRateLimitPerMin(visibleLimit, unit);
    onChange(createLimitSetting(isChecked, updatedRateLimitPerMin));
  };

  useUpdateEffect(() => {
    handleLimitUpdate();
  }, [isChecked, unit]);

  return (
    <ExpandableCheckbox
      data-test={RATE_LIMIT_TEST_ID}
      caption="Limit how fast Iterable will send this campaign."
      label="Set a send rate limit"
      id="rate-limiting"
      isChecked={isChecked}
      onEnabledUpdate={setIsChecked}
      isDisabled={isDisabled}
    >
      <>
        <IndentedRowWrapper>
          <SmallInputWrapper>
            <WideInput
              hasError={rangeError.hasError}
              id={RATE_LIMIT_INPUT_TEST_ID}
              data-test={RATE_LIMIT_INPUT_TEST_ID}
              onBlur={handleLimitUpdate}
              onChange={(newLimit: string) => handleLimitChange(Number(newLimit))}
              placeholder={`${DEFAULT_LIMIT}`}
              Suffix={<span>{visibleLimit === 1 ? 'message' : 'messages'}</span>}
              type="number"
              value={`${visibleLimit}`}
              isDisabled={isDisabled}
            />
          </SmallInputWrapper>
          <SeparationText> per </SeparationText>
          <TinyBasicSelect
            options={rateUnitOptions}
            defaultValues={[unit]}
            onSelect={([selectedUnit]) => onUnitSelect(selectedUnit as RATE_LIMIT_UNITS)}
            isDisabled={isDisabled}
          />
        </IndentedRowWrapper>
        {rangeError.hasError && <ErrorText>{rangeError.message}</ErrorText>}
      </>
    </ExpandableCheckbox>
  );
};

export default RateLimitingCheckbox;
